/**
 * Indirection for obtaining the OpenCV runtime.
 *
 * The runtime has to be loaded two different ways: the main thread uses a
 * `<script>` tag, a classic worker uses `importScripts`. Rather than thread the
 * loader through every call, each environment registers a provider here on
 * import and `inpaint.ts` just asks for the runtime.
 *
 * Keeping this separate also means the "never let the emscripten Module travel
 * through a promise resolution" rule is enforced in exactly one place per
 * environment instead of being re-derived at each call site.
 */

import type { OpenCvRuntime } from './openCvRuntime';

type Loader = () => Promise<void>;
type Getter = () => OpenCvRuntime;

let load: Loader | null = null;
let get: Getter | null = null;

export function registerOpenCvProvider(loader: Loader, getter: Getter): void {
  load = loader;
  get = getter;
}

export async function ensureOpenCv(): Promise<void> {
  if (!load) {
    throw new Error('No OpenCV provider registered. The engine cannot be loaded here.');
  }
  await load();
}

export function requireOpenCv(): OpenCvRuntime {
  if (!get) {
    throw new Error('No OpenCV provider registered. The engine cannot be loaded here.');
  }
  return get();
}